import { Button, message, Input, DatePicker, Table, Select, Space, Switch } from "antd";
import React, { useEffect, useState } from "react";
import API from "../../api";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import CustomTable from "../../components/Table/CustomTable";
import { getUserRole } from "../../utils/auth";

// Extend Day.js with the plugin
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Option } = Select;

export default function SalesReport() {
  const [sales, setSales] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false); // Fixed: `loading` should be a boolean, not an array
  const [balanceFilter, setBalanceFilter] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({
    storeName: null,
    customerName: null,
    search: null,
    fromDate: null,
    toDate: null,
    financialYear: null, // New: Financial year filter
  });
  const [userRole, setUserRole] = useState("");

  useEffect(() => {
    setUserRole(getUserRole().role);
  }, []);
  // Generate financial years starting from 2024-2025 to current financial year (April-based)
  const generateFinancialYears = () => {
    const currentDate = new Date();
    const financialYearStart = new Date(currentDate.getFullYear(), 3, 1); // April 1st

    let currentFinancialYearStart, currentFinancialYearEnd;
    if (currentDate < financialYearStart) {
      // If before April, the current financial year is the previous year
      currentFinancialYearStart = currentDate.getFullYear() - 1;
      currentFinancialYearEnd = currentDate.getFullYear();
    } else {
      // If April or later, the current financial year is this year
      currentFinancialYearStart = currentDate.getFullYear();
      currentFinancialYearEnd = currentDate.getFullYear() + 1;
    }

    const financialYears = [];
    let startYear = 2024; // Start from 2024
    let endYear = startYear + 1;

    // Generate financial years from 2024-2025 up to the current financial year
    while (startYear <= currentFinancialYearStart) {
      financialYears.push(`${startYear}-${endYear}`);
      startYear++;
      endYear++;
    }

    return financialYears; // Return in ascending order (oldest to newest)
  };

  const handleSearchChange = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value ? value : null,
    }));
  };

  const getSales = async (page = 1, pageSize = 20, filters = {}) => {
    try {
      setLoading(true);
      const { storeName, search, fromDate, toDate, customerName, financialYear } = filters;
      const response = await API.get("/sales/paginate", {
        params: {
          page,
          limit: pageSize,
          storeName,
          search,
          customerName,
          balance: balanceFilter ? true : undefined,
          fromDate: fromDate ? dayjs(fromDate).format('YYYY-MM-DD') : null,
          toDate: toDate ? dayjs(toDate).format('YYYY-MM-DD') : null,
          financialYear: financialYear || null, // Pass financial year to API
        },
      });

      const salesData = Array.isArray(response?.data?.sales) ? response.data.sales : [];
      setSales(salesData);

      setPaginationInfo({
        current: page,
        pageSize: pageSize,
        total: response?.data?.pagination?.total || 0,
      });
    } catch (err) {
      console.error("Error fetching sales:", err);
      message.error("Failed to load sales.");
    } finally {
      setLoading(false);
    }
  };

  const getStore = async () => {
    try {
      const response = await API.get("/stores");
      setStores(response?.data || []);
      // Set initial storeName based on userData
      const userData = JSON.parse(localStorage.getItem('userData')) || {};
      const userStoreId = userData.storeId;
      const userRoleValue = getUserRole();
      setUserRole(userRoleValue.role)
      if (userRoleValue.role !== 'admin' && userStoreId) {
        const matchingStore = response.data.find((store) => store._id === userStoreId);
        if (matchingStore) {
          setFilters((prev) => ({ ...prev, storeName: matchingStore.name }));
        }
      }
    } catch (err) {
      console.error("Error fetching stores:", err);
      message.error("Failed to load stores.");
    }
  };

  useEffect(() => {
    getStore();
    const data = localStorage.getItem('userData');
    const role = JSON.parse(data);
    setUserRole(role);
  }, []);

  useEffect(() => {
    getSales(paginationInfo.current, paginationInfo.pageSize, filters);
  }, [filters, paginationInfo.current, paginationInfo.pageSize, balanceFilter]);

  const handleDownload = async (data) => {
    try {
      const result = await API.get(`/sales/generate-invoice/${data?._id}`);
      if (result.status === 200) {
        message.success("Invoice generated successfully.");
        const getPdf = await API.get(result.data.downloadLink, {
          responseType: "arraybuffer",
        });
        const blob = new Blob([getPdf.data], { type: "application/pdf" });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `invoice_${data?.invoiceId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to generate invoice.");
    }
  };

  const formatFinanceName = (finance) => {
    if (!finance) return null; // Handle undefined or null case
    // Split camelCase into words and capitalize each word
    const words = finance
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before capital letters
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
    return words.join(' ');
  };

  const columns = [
    {
      title: "Index",
      key: "index",
      render: (_, __, index) =>
        paginationInfo.total -
        ((paginationInfo.current - 1) * paginationInfo.pageSize + index),
    },
    {
      title: "Invoice No.",
      render: (_, sale) => sale?.invoiceId,
      key: "invoiceId",
      filterDropdown: () => (
        <Input
          placeholder="Search Invoice"
          onChange={(e) => handleSearchChange('search', e.target.value)}
          value={filters.search}
          suffix={<SearchOutlined />}
        />
      ),
      filterIcon: <SearchOutlined />,
    },
    {
      title: "Inv. Date",
      key: "orderDate",
      render: (_, sale) => dayjs(sale?.orderDate).format('DD-MM-YYYY'),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <DatePicker.RangePicker
            value={selectedKeys && selectedKeys.length > 0 ? [dayjs(selectedKeys[0]), dayjs(selectedKeys[1])] : []}
            onChange={(dates) => {
              setSelectedKeys(dates ? [dates[0]?.format("YYYY-MM-DD"), dates[1]?.format("YYYY-MM-DD")] : []);
            }}
            format="YYYY-MM-DD"
          />
          <div style={{ marginTop: 8 }}>
            <Button
              type="primary"
              size="small"
              style={{ width: 90, marginRight: 8 }}
              onClick={() => {
                confirm();
                if (selectedKeys && selectedKeys.length > 1) {
                  handleSearchChange("fromDate", selectedKeys[0]);
                  handleSearchChange("toDate", selectedKeys[1]);
                }
              }}
            >
              Filter
            </Button>
            <Button
              size="small"
              style={{ width: 90 }}
              onClick={() => {
                clearFilters();
                setSelectedKeys([]);
                handleSearchChange("fromDate", null);
                handleSearchChange("toDate", null);
              }}
            >
              Reset
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => <SearchOutlined />,
    },
    {
      title: "Inv. Amount (₹)",
      key: "totalAmount",
      render: (_, sale) => `₹ ${(new Intl.NumberFormat().format(sale?.totalAmount))}`,
    },
    {
      title: "Financier",
      key: "finance",
      render: (_, sale) => formatFinanceName(sale?.finance),
    },
    {
      title: "Paid (₹)",
      key: "totalAmount",
      render: (_, sale) => `₹ ${(new Intl.NumberFormat().format(sale?.paidAmount))}`,
    },
    {
      title: "Balance (₹)",
      key: "totalAmount",
      render: (_, sale) => (
        <div style={{ flexDirection: 'column' }}>
          <span
            style={{
              color: sale?.balanceAmount - sale?.balancePaid > 0 ? "red" : "green",
            }}
          >
            ₹ {new Intl.NumberFormat().format(sale?.balanceAmount || 0)}
          </span>
          {sale?.balanceAmount > 0 && (
            <>
              <br />
              <span style={{ color: "black" }}>Paid: </span>
              <span style={{ color: sale?.balanceAmount - sale?.balancePaid > 0 ? "red" : "green" }}>
                ₹ {new Intl.NumberFormat().format(sale?.balancePaid || 0)}
              </span>
              <br />
              <span style={{ color: "black" }}>Remaining: </span>
              <span style={{ color: sale?.balanceAmount - sale?.balancePaid > 0 ? "red" : "green" }}>
                ₹ {new Intl.NumberFormat().format((sale?.balanceAmount - sale?.balancePaid) || 0)}
              </span>
            </>
          )}
        </div>
      ),
    },
    {
      title: "Pay Mode",
      key: "totalAmount",
      render: (_, sale) => `${sale?.paymentMethod?.toUpperCase() || '--'}`,
    },
    {
      title: "Customer",
      key: "customerName",
      render: (_, sale) => (
        <div>
          <div style={{ fontWeight: "bold", fontSize: "12px" }}>
            {sale?.customerId?.name || "N/A"}
          </div>
          <div style={{ color: "gray", fontSize: "12px" }}>
            {sale?.customerId?.phone || "---"}
          </div>
        </div>
      ),
      filterDropdown: () => (
        <Input
          placeholder="Search Customer"
          onChange={(e) => handleSearchChange("customerName", e.target.value)}
          value={filters.customerName}
          suffix={<SearchOutlined />}
        />
      ),
    },
    {
      title: "Store",
      key: "storeName",
      render: (_, sale) => (
        <div>
          <div style={{ fontWeight: "bold", color: '#1591EA', fontSize: "12px" }}>
            {sale?.storeId?.name || "N/A"}
          </div>
        </div>
      ),
      filterDropdown: () => (
        <Input
          placeholder="Search Customer"
          onChange={(e) => handleSearchChange("customerName", e.target.value)}
          value={filters.customerName}
          suffix={<SearchOutlined />}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color;
        if (status === "pending") {
          color = "yellow";
        } else if (status === "completed") {
          color = "green";
        } else if (status === "cancelled") {
          color = "red";
        }
        return (
          <span style={{ color: color, fontWeight: "bold" }}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, sale) => (
        <div>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(sale)}
            style={{ marginRight: "8px", color: 'green' }}
          />
        </div>
      ),
    },
  ];

  return (
    <div style={{
      padding: "20px",
      background: "#fff",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    }}>
      <div
        style={{
          fontWeight: "bold",
          fontSize: "20px",
          textAlign: "center",
          marginBottom: "10px",
          color: "#1890ff",
        }}
      >
        Sales Report
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "5px 10px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          marginBottom: "10px",
        }}
      >
        <div style={{ fontSize: "16px", fontWeight: "500" }}>
          Filter by Store:
        </div>
        <Space>
          <Switch
            checked={balanceFilter}
            onChange={(checked) => {
              setBalanceFilter(checked);
            }}
          />
          <Select
            style={{ width: 150 }}
            placeholder="Store"
            value={filters.storeName}
            onChange={(value) => handleSearchChange("storeName", value)}
            allowClear
            disabled={userRole !== 'admin'}

          >
            {stores.map((store) => (
              <Select.Option key={store._id} value={store.name}>
                {store.name}
              </Select.Option>
            ))}
          </Select>
          <Select
            style={{ width: 150 }}
            placeholder="Select Financial Year"
            value={filters.financialYear}
            onChange={(value) => handleSearchChange("financialYear", value)}
            allowClear
          >
            {generateFinancialYears().map((year) => (
              <Select.Option key={year} value={year}>
                {year}
              </Select.Option>
            ))}
          </Select>
        </Space>
      </div>

      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "8px",
          padding: "20px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <CustomTable
          columns={columns}
          dataSource={sales}
          onPaginationChange={(pagination) => {
            setPaginationInfo({
              current: pagination.current,
              pageSize: pagination.pageSize,
            });
          }}
          loading={loading}
          style={{ padding: "1 1px", width: "100%" }}
          total={paginationInfo.total}
        />
      </div>
    </div>
  );
}